import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from '../utils/toast';
import { forgotPassword } from '../services/users';
import LanguageSwitcher from '../components/languageSwitcher';
import './auth.css';

const RESEND_COOLDOWN_S = 60

const ForgotPasswordPage = () => {

    const { t } = useTranslation();

    // Prefilled when the login page sends an account here to set a new password.
    const [searchParams] = useSearchParams()
    const [email, setEmail] = useState(searchParams.get('email') || '')
    const [submittedEmail, setSubmittedEmail] = useState('')
    const [isSending, setIsSending] = useState(false)
    const [cooldown, setCooldown] = useState(0)
    // status: 'idle' | 'sent'
    const [status, setStatus] = useState('idle')

    useEffect(() => {
        if (cooldown <= 0) return
        const id = setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000)
        return () => clearInterval(id)
    }, [cooldown])

    const handleSubmit = async (e) => {
        if (e) e.preventDefault()
        if (!email || isSending || cooldown > 0) return
        setIsSending(true)
        try {
            await forgotPassword(email)
            setSubmittedEmail(email)
            setStatus('sent')
            setCooldown(RESEND_COOLDOWN_S)
        } catch (err) {
            toast.error(t('auth.forgotPasswordError'), {
                position: toast.POSITION.BOTTOM_RIGHT
            })
        } finally {
            setIsSending(false)
        }
    }

    const handleResend = async () => {
        if (cooldown > 0 || isSending) return
        setIsSending(true)
        try {
            await forgotPassword(submittedEmail)
            toast.success(t('auth.resendSuccess'), {
                position: toast.POSITION.BOTTOM_RIGHT
            })
            setCooldown(RESEND_COOLDOWN_S)
        } catch (err) {
            toast.error(t('auth.resendError'), {
                position: toast.POSITION.BOTTOM_RIGHT
            })
        } finally {
            setIsSending(false)
        }
    }

    return (
        <div className="auth-page">
            <div className="auth-shell">

                {/* ───────── LEFT / BRAND PANEL ───────── */}
                <aside className="auth-brand-panel">
                    <div className="auth-brand-top">
                        <Link to="/login" className="auth-brand-link" aria-label={t('auth.logoGoToSignIn')}>
                            <img
                                src={process.env.PUBLIC_URL + '/logo-soroby.svg'}
                                alt="Soroby"
                                className="auth-brand-logo"
                            />
                        </Link>
                    </div>

                    <div className="auth-brand-message">
                        <h1 className="auth-brand-title" dangerouslySetInnerHTML={{ __html: t('auth.brandTitle') }} />
                        <p className="auth-brand-tagline">
                            {t('auth.brandTagline')}
                        </p>
                    </div>

                    <div className="auth-brand-footer">
                        <span>© {new Date().getFullYear()} Soroby · {t('auth.builtForServiceBusinesses')}</span>
                        <span>soroby.com</span>
                    </div>
                </aside>

                {/* ───────── RIGHT / FORM OR SUCCESS PANEL ───────── */}
                <main className="auth-form-panel">
                    <LanguageSwitcher className="auth-lang" id="auth-language-drop" drop="down" align="end" showIcon />

                    <div className="auth-form-inner">

                        <div className="auth-mobile-brand">
                            <Link to="/login" className="auth-brand-link" aria-label={t('auth.logoGoToSignIn')}>
                                <img src={process.env.PUBLIC_URL + '/logo-soroby.svg'} alt="Soroby" />
                            </Link>
                        </div>

                        {status === 'idle' && (
                            <>
                                <div className="auth-form-heading">
                                    <h2>{t('auth.forgotPasswordTitle')}</h2>
                                    <p>{t('auth.forgotPasswordSubtitle')}</p>
                                </div>

                                <form className="auth-form" onSubmit={handleSubmit}>
                                    <div className="auth-field">
                                        <label className="auth-label" htmlFor="forgot-email">{t('auth.emailOrUsername')}</label>
                                        <div className="auth-input-wrap">
                                            <svg className="auth-input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                <rect x="3" y="5" width="18" height="14" rx="2" />
                                                <path d="M3 7l9 6 9-6" />
                                            </svg>
                                            <input
                                                id="forgot-email"
                                                className="auth-input"
                                                type="text"
                                                autoComplete="username"
                                                autoCapitalize="none"
                                                placeholder={t('auth.emailOrUsernamePlaceholder')}
                                                required
                                                value={email}
                                                disabled={isSending}
                                                onChange={(e) => setEmail(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <button
                                        type="submit"
                                        className="auth-btn"
                                        disabled={!email || isSending}
                                    >
                                        {isSending ? (
                                            <span>{t('auth.sending')}</span>
                                        ) : (
                                            <>
                                                <span>{t('auth.sendResetLink')}</span>
                                                <span className="arrow" aria-hidden="true">→</span>
                                            </>
                                        )}
                                    </button>
                                </form>

                                <p className="auth-secondary">
                                    {t('auth.rememberedIt')} <Link to="/login">{t('auth.backToSignIn')}</Link>.
                                </p>
                            </>
                        )}

                        {status === 'sent' && (
                            <>
                                <div className="auth-success-icon" aria-hidden="true">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="3" y="5" width="18" height="14" rx="2" />
                                        <path d="M3 7l9 6 9-6" />
                                    </svg>
                                </div>

                                <div className="auth-form-heading">
                                    <h2>{t('auth.checkYourInbox')}</h2>
                                    <p>
                                        {t('auth.resetLinkSent')}
                                    </p>
                                </div>

                                <div className="auth-email-chip">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                        <rect x="3" y="5" width="18" height="14" rx="2" />
                                        <path d="M3 7l9 6 9-6" />
                                    </svg>
                                    <span>{submittedEmail}</span>
                                </div>

                                <Link to="/login" className="auth-btn">
                                    <span>{t('auth.backToSignIn')}</span>
                                    <span className="arrow" aria-hidden="true">→</span>
                                </Link>

                                <p className="auth-secondary">
                                    {t('auth.didntGetEmail')} {t('auth.checkSpamOr')}{' '}
                                    <button
                                        type="button"
                                        className="auth-inline-btn"
                                        onClick={handleResend}
                                        disabled={cooldown > 0 || isSending}
                                    >
                                        {isSending
                                            ? t('auth.sending').toLowerCase()
                                            : cooldown > 0
                                                ? t('auth.resendInSeconds', { seconds: cooldown })
                                                : t('auth.resendIt')}
                                    </button>
                                    .
                                </p>
                            </>
                        )}

                    </div>
                </main>

            </div>
        </div>
    )
}

export default ForgotPasswordPage;
